import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SecureImage } from "@/components/SecureImage";
import { EmptyState, Loader } from "@/components/EmptyState";
import { StatusBadge } from "@/components/StatusBadge";
import { ProfileCodeChip } from "@/components/ProfileCodeChip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Handshake, Plus, Search, Store, Trash2 } from "lucide-react";
import { BUSINESS_TYPE_LABELS } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { PartnerRequests } from "@/components/org/PartnerRequests";

export default function Partners() {
  const { user } = useAuth();
  const canManage = can(user, "partners.create");
  const canApprove = can(user, "partners.approve");
  const canDelete = can(user, "partners.delete");
  const [partners, setPartners] = useState([]);
  const [reqs, setReqs] = useState({ incoming: [], outgoing: [] });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);

  const load = async () => {
    const r = await api.get("/partners");
    setPartners(r.data.partners); setReqs({ incoming: r.data.incoming || [], outgoing: r.data.outgoing || [] }); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Loader />;

  return (
    <div className="space-y-6" data-testid="partners-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-stone-900 sm:text-3xl">Partners</h1>
          <p className="mt-1 text-stone-500">Send a connect request using a business's profile code. Once they accept, you can trade.</p>
        </div>
        {canManage && <Button data-testid="add-partner-btn" onClick={() => setModal(true)} className="bg-[#0D5C3A] hover:bg-[#084229]"><Plus className="mr-1 h-4 w-4" /> Add Partner</Button>}
      </div>

      <PartnerRequests incoming={reqs.incoming} outgoing={reqs.outgoing} onChanged={load} canAct={canApprove} canCancel={canManage} />

      {partners.length === 0 ? (
        <EmptyState icon={Handshake} title="No partners yet" description="Send a connect request by entering their RV profile code." testid="partners-empty"
          action={canManage && <Button data-testid="empty-add-partner-btn" onClick={() => setModal(true)} className="bg-[#0D5C3A] hover:bg-[#084229]"><Plus className="mr-1 h-4 w-4" /> Add Partner</Button>} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {partners.map((p) => (
            <div key={p.rel_id} className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm" data-testid={`partner-card-${p.profile_code}`}>
              <div className="flex items-center gap-3">
                <SecureImage fileId={p.logo_file_id} className="h-12 w-12 rounded-xl object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading font-semibold text-stone-900">{p.name}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0D5C3A]">
                    <Store className="h-3 w-3" /> {BUSINESS_TYPE_LABELS[p.business_type] || p.business_type}
                  </span>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <ProfileCodeChip code={p.profile_code} />
                <span className="text-xs text-stone-400">{p.order_count} orders</span>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-[#F0ECE1] pt-3">
                <StatusBadge status="Accepted" />
                {canDelete && <RemovePartner id={p.rel_id} onDone={load} />}
              </div>
            </div>
          ))}
        </div>
      )}
      <AddPartnerModal open={modal} onOpenChange={setModal} onSaved={load} />
    </div>
  );
}

function RemovePartner({ id, onDone }) {
  const remove = async () => { await api.delete(`/partners/${id}`); toast.success("Partner removed"); onDone(); };
  return <button onClick={remove} className="rounded-lg p-2 text-rose-500 hover:bg-rose-50" data-testid={`partner-remove-${id}`}><Trash2 className="h-4 w-4" /></button>;
}

function AddPartnerModal({ open, onOpenChange, onSaved }) {
  const [code, setCode] = useState("");
  const [found, setFound] = useState(null);
  const [conn, setConn] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setCode(""); setFound(null); setConn(null); } }, [open]);

  const search = async () => {
    setBusy(true); setFound(null);
    try { const r = await api.post("/partners/search", { code }); setFound(r.data.business); setConn(r.data.connection_status); }
    catch (e) { toast.error(e.response?.data?.detail || "Not found"); } finally { setBusy(false); }
  };
  const add = async () => {
    setBusy(true);
    try { await api.post("/partners", { partner_business_id: found.id }); toast.success("Connect request sent", { description: `${found.name} needs to accept it.` }); onSaved(); onOpenChange(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="partner-modal">
        <DialogHeader><DialogTitle>Add Partner</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Partner Profile Code</Label>
            <div className="mt-1 flex gap-2">
              <Input data-testid="partner-code-input" placeholder="RV-MFR-10001" value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} />
              <Button data-testid="partner-search-btn" disabled={busy || !code} onClick={search} className="bg-[#0D5C3A] hover:bg-[#084229]"><Search className="h-4 w-4" /></Button>
            </div>
          </div>
          {found && (
            <div className="rounded-xl border border-[#E5E0D8] bg-[#FAF8F5] p-4" data-testid="partner-search-result">
              <div className="flex items-center gap-3">
                <SecureImage fileId={found.logo_file_id} className="h-12 w-12 rounded-xl object-cover" />
                <div>
                  <p className="font-heading font-semibold text-stone-900">{found.name}</p>
                  <p className="font-mono text-xs text-stone-500">{found.profile_code} · {BUSINESS_TYPE_LABELS[found.business_type] || found.business_type}</p>
                </div>
              </div>
              {conn ? (
                <p className="mt-4 rounded-lg bg-white px-3 py-2 text-center text-sm font-medium text-stone-600" data-testid="partner-connection-status">{conn === "connected" ? "You're already connected" : "A connect request is already pending"}</p>
              ) : (
                <Button data-testid="partner-confirm-add-btn" disabled={busy} onClick={add} className="mt-4 w-full bg-[#C85A32] hover:bg-[#a8481f]">Send Connect Request</Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
